import { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button, Label, TextInput, Alert } from 'flowbite-react';
import { HiMail, HiLockClosed } from 'react-icons/hi';

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn, signInWithGoogle, userProfile, refreshUserProfile } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Mostrar mensaje si viene de otra página
  useEffect(() => {
    if (location.state?.message) {
      // Usar alert nativo o implementar toast con Flowbite
      alert(location.state.message);
    }
  }, [location]);

  // Verificar estado del usuario después de login
  useEffect(() => {
    if (userProfile) {
      console.log('UserProfile actualizado:', userProfile);
      if (userProfile.status === 'pending_approval') {
        console.log('Redirigiendo a waiting-approval');
        navigate('/waiting-approval');
      } else if (userProfile.status === 'active') {
        console.log('Redirigiendo a dashboard');
        navigate('/dashboard');
      } else if (userProfile.status === 'inactive') {
        setError('Tu cuenta está inactiva. Contacta al administrador.');
      }
    } else {
      console.log('UserProfile es null');
    }
  }, [userProfile, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      console.log('Iniciando login...');
      await signIn(email, password);
      console.log('Login exitoso, esperando actualización de perfil...');
      
      await new Promise(resolve => setTimeout(resolve, 800));
      
      console.log('Refrescando perfil...');
      await refreshUserProfile();
      
      await new Promise(resolve => setTimeout(resolve, 500));
      
      console.log('Perfil refrescado, esperando redirección...');
      
      setTimeout(() => {
        if (userProfile) {
          console.log('Timeout: redirigiendo manualmente con perfil:', userProfile);
          if (userProfile.status === 'active') {
            navigate('/dashboard');
          } else if (userProfile.status === 'pending_approval') {
            navigate('/waiting-approval');
          }
        } else {
          console.log('Timeout: no hay perfil, redirigiendo a dashboard de todas formas');
          navigate('/dashboard');
        }
        setLoading(false);
      }, 3000);
      
    } catch (err: any) {
      console.error('Error en login:', err);
      const errorMessage = err.message || 'Error al iniciar sesión';
      setError(errorMessage);
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError('');
    setLoading(true);

    try {
      await signInWithGoogle('signin');
    } catch (err: any) {
      setError(err.message || 'Error al iniciar sesión con Google');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 px-4 py-12">
      <div className="w-full max-w-md">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-8 text-center">
            <div className="flex justify-center mb-4">
              <img 
                src="/logo.png" 
                alt="Domus Secura" 
                className="h-16 w-16 object-contain bg-white rounded-lg p-2 shadow-lg"
                onError={(e) => {
                  // Fallback a logo.svg si logo.png no existe
                  const target = e.target as HTMLImageElement;
                  target.src = '/logo.svg';
                }}
              />
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">Iniciar Sesión</h1>
            <p className="text-blue-100">Accede a tu cuenta de Domus Secura</p>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {error && (
              <Alert color="failure" className="mb-4">
                <span className="font-medium">{error}</span>
              </Alert>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="email">Email</Label>
                <TextInput
                  id="email"
                  type="email"
                  icon={HiMail}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="tu@email.com"
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="password">Contraseña</Label>
                <TextInput
                  id="password"
                  type="password"
                  icon={HiLockClosed}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="••••••••"
                  className="mt-1"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-blue-600 hover:bg-blue-700"
                size="lg"
              >
                {loading ? 'Iniciando sesión...' : 'Iniciar Sesión'}
              </Button>
            </form>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300 dark:border-gray-600"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400">O continúa con</span>
              </div>
            </div>

            <Button
              onClick={handleGoogleSignIn}
              disabled={loading}
              color="gray"
              className="w-full text-gray-900 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600"
              size="lg"
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path
                  fill="currentColor"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="currentColor"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="currentColor"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="currentColor"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              Continuar con Google
            </Button>

            <p className="text-center text-sm text-gray-600 dark:text-gray-400">
              ¿No tienes cuenta?{' '}
              <Link to="/register" className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 hover:underline font-semibold">
                Regístrate
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
